import type { PointerEvent, ReactNode } from "react";
import { FiCreditCard, FiDollarSign } from "react-icons/fi";
import { CardActionMenu, type CardAction } from "@/components/ui/CardActionMenu";

export type AccountCardTone = "mist" | "lilac" | "lime" | "sun";

export type AccountCardAction = CardAction;

interface AccountCardProps {
  name: string;
  typeLabel: string;
  tone: AccountCardTone;
  color?: string | null;
  balanceLabel: string;
  balance: string;
  accessibleLabel: string;
  incomeLabel: string;
  income: string;
  expenseLabel: string;
  expense: string;
  creditInfo?: ReactNode;
  progress?: { percent: number; label: string; accessibleLabel: string; valueText: string };
  creditDates?: { cut?: string; payment?: string };
  actions: AccountCardAction[];
  menuLabel: string;
  onOpen: () => void;
  onPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerMove?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerUp?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerLeave?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerCancel?: (event: PointerEvent<HTMLButtonElement>) => void;
  isSwiped?: boolean;
}

export const AccountCard = ({
  name,
  typeLabel,
  tone,
  color,
  balanceLabel,
  balance,
  accessibleLabel,
  incomeLabel,
  income,
  expenseLabel,
  expense,
  creditInfo,
  progress,
  creditDates,
  actions,
  menuLabel,
  onOpen,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  isSwiped = false,
}: AccountCardProps) => {
  const background = color ?? `var(--ds-card-${tone})`;
  const Icon = tone === "sun" || tone === "lime" ? FiDollarSign : FiCreditCard;
  const offset = isSwiped ? actions.length * 5 : 0;
  return (
    <article
      className="relative touch-pan-y rounded-[var(--ds-radius-lg)] text-[var(--ds-on-card)]"
      style={{ backgroundColor: background }}
    >
      <div className="relative overflow-hidden rounded-[var(--ds-radius-lg)]">
        <div
          className={`absolute inset-y-0 right-0 flex ${isSwiped ? "visible" : "invisible"}`}
          aria-hidden={!isSwiped}
        >
          {actions.map((action) => (
            <button
              key={action.kind}
              type="button"
              disabled={action.disabled || action.loading}
              aria-label={`${action.label} ${name}`}
              tabIndex={isSwiped ? 0 : -1}
              onClick={action.onClick}
              className={`inline-flex min-h-11 w-20 items-center justify-center text-sm font-medium disabled:opacity-50 ${
                action.kind === "delete"
                  ? "bg-[var(--ds-card-flame)] text-[var(--ds-on-card)]"
                  : "bg-[var(--ds-card-sun)] text-[var(--ds-on-card)]"
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          aria-label={accessibleLabel}
          onClick={onOpen}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onPointerCancel={onPointerCancel}
          className="app-focus-ring relative block w-full min-w-0 rounded-[var(--ds-radius-lg)] p-5 text-left transition-transform duration-300 md:p-6"
          style={{ transform: `translateX(-${offset}rem)` }}
        >
          <span className="flex min-w-0 items-center gap-3 pr-30">
            <span
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--ds-on-card)]"
              style={{ color: background }}
              aria-hidden="true"
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1 truncate text-[17px] font-medium leading-[22px]">{name}</span>
          </span>

          <span className="mt-5 block min-w-0">
            <span className="block text-[11px] font-medium uppercase leading-[14px] tracking-[0.08em] text-[var(--ds-on-card-muted)]">{balanceLabel}</span>
            <span className="mt-0.5 block truncate font-[var(--ds-font-display)] text-[40px] font-normal leading-[44px] tracking-[-0.035em]">{balance}</span>
            {creditInfo ? <span className="account-card-credit-info block text-sm leading-5">{creditInfo}</span> : null}
            {progress ? (
              <>
                <span
                  role="progressbar"
                  aria-label={progress.accessibleLabel}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.max(0, Math.min(100, Math.round(progress.percent)))}
                  aria-valuetext={progress.valueText}
                  className="account-card-credit-progress block h-2 overflow-hidden rounded-full bg-[color:color-mix(in_srgb,var(--ds-on-card)_18%,transparent)]"
                >
                  <span
                    className={`block h-full rounded-full bg-[var(--ds-on-card)] ${progress.percent > 0 ? "min-w-2" : ""}`}
                    style={{ width: `${Math.max(0, Math.min(100, progress.percent))}%` }}
                  />
                </span>
                <span className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px] font-medium leading-[18px]">
                  <span>{progress.label}</span>
                  {creditDates?.cut ? <span>{creditDates.cut}</span> : null}
                  {creditDates?.payment ? <span>{creditDates.payment}</span> : null}
                </span>
              </>
            ) : null}
          </span>

          <span className="mt-4 grid grid-cols-2 gap-3 border-t border-[color:color-mix(in_srgb,var(--ds-on-card)_22%,transparent)] pt-3">
            <span>
              <span className="block text-[11px] font-medium uppercase leading-[14px] tracking-[0.08em] text-[var(--ds-on-card-muted)]">{incomeLabel}</span>
              <span className="block truncate text-base font-medium leading-[22px]">{income}</span>
            </span>
            <span>
              <span className="block text-[11px] font-medium uppercase leading-[14px] tracking-[0.08em] text-[var(--ds-on-card-muted)]">{expenseLabel}</span>
              <span className="block truncate text-base font-medium leading-[22px]">{expense}</span>
            </span>
          </span>
        </button>

      </div>
      <div className="absolute right-3 top-[18px] z-20 flex items-center gap-2 md:right-4 md:top-[22px]">
        <span aria-hidden="true" className="inline-flex items-center rounded-full border-[1.5px] border-current account-card-type text-xs font-medium leading-4">
          {typeLabel}
        </span>
        <CardActionMenu name={name} menuLabel={menuLabel} actions={actions} />
      </div>
    </article>
  );
};
